import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Building2, BarChart3, Plus, Trash2, Pencil, Save, X, ExternalLink, Info, ChevronDown, Check, Ban } from "lucide-react";
import api from "../lib/api";
import { SITE } from "../lib/site";

const TABS = [
  { key: "cards", label: "Credit Cards", icon: <CreditCard className="w-4 h-4" /> },
  { key: "partners", label: "Partners (Hotels/Other)", icon: <Building2 className="w-4 h-4" /> },
  { key: "clicks", label: "Click Analytics", icon: <BarChart3 className="w-4 h-4" /> },
];

const CARD_TEXT = [
  ["airline_name", "Airline *"], ["loyalty_program", "Loyalty program"], ["card_name", "Card name"],
  ["issuer", "Issuer (bank)"], ["card_network", "Card network"], ["annual_fee", "Annual fee"],
  ["welcome_offer", "Welcome offer"], ["spending_requirement", "Spending requirement"],
  ["lounge_access", "Lounge access"], ["companion_benefit", "Companion benefit"],
  ["offer_start", "Offer start"], ["offer_end", "Offer end"], ["last_verified", "Last verified"],
  ["affiliate_network", "Affiliate network"], ["affiliate_id", "Affiliate ID"],
];
const CARD_URLS = [
  ["loyalty_official_url", "① Loyalty program official URL"],
  ["official_card_url", "② Card official URL (issuer)"],
  ["affiliate_url", "③ Affiliate application URL (tracked)"],
];
const CARD_BOOLS = [["free_checked_bag", "Free checked bag"], ["priority_boarding", "Priority boarding"], ["disclosure_required", "Disclosure required"], ["active", "Active (show publicly)"]];

export default function AdminAffiliates() {
  const [tab, setTab] = useState("cards");
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-affiliates">
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight">Card & Affiliate Manager</h1>
      <p className="text-muted-foreground mt-2 max-w-3xl">Manage airline co-branded credit cards, travel partners (hotels/other) and their tracked affiliate links. Public pages display active items only, ranked by {SITE.name} Score.</p>

      <StrategyPanel />

      <div className="flex flex-wrap gap-1 border-b border-zinc-200 mt-6 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-600 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-zinc-900"}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "cards" && <CardsTab />}
      {tab === "partners" && <PartnersTab />}
      {tab === "clicks" && <ClicksTab />}
    </div>
  );
}

function StrategyPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-200 bg-blue-50/40 mt-6">
      <button onClick={() => setOpen((o) => !o)} data-testid="admin-strategy-toggle" className="w-full flex items-center gap-2 p-4 text-left">
        <Info className="w-5 h-5 text-primary shrink-0" />
        <span className="font-600 text-blue-900 flex-1">Affiliate strategy playbook (how to structure cards & links)</span>
        <ChevronDown className={`w-4 h-4 text-primary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-5 text-sm text-blue-950/80 space-y-3">
          <p><strong>Start with credit-card affiliate networks</strong> (one relationship → many cards): CardRatings, Bankrate/CardMatch, FlexOffers, CJ Affiliate, Impact, Partnerize, Awin, Skimlinks. Then approach issuers directly — Amex (Delta), Citi (American, from 2026), Chase (United/Southwest), Bank of America (Alaska), Barclays (JetBlue).</p>
          <p><strong>Keep three separate links per card:</strong> ① Loyalty program official page · ② Card official (issuer) info · ③ Your tracked affiliate application URL. This keeps the database resilient when banks or networks change.</p>
          <p><strong>Track every click</strong> — all affiliate links route through <code className="bg-white px-1 border">/api/go/&#123;slug&#125;</code>, which logs airline, card, page, time, country and device, then redirects. Never store card-application data.</p>
          <p><strong>Offers are dynamic</strong> — always update Welcome offer + Last verified + Offer end. Don't hard-code a bonus permanently.</p>
          <p><strong>Rank by traveler value, not commission.</strong> The {SITE.name} Score drives ordering; the disclosure states compensation does not determine the score. Keep the site trustworthy — an information platform with an optional travel-benefits section, not a card-sales site.</p>
          <p><strong>Phased launch:</strong> P1 = 10 US airlines (shells pre-created below) → P2 = major international → P3 = global marketplace + My Travel Hub integration.</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, textarea }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
      )}
    </label>
  );
}

function CardsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/cards").then((r) => setItems(r.data.items)).catch(() => toast.error("Failed to load cards"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.airline_name?.trim()) { toast.error("Airline is required"); return; }
    try { await api.post("/admin/cards", editing); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this card?")) return; await api.delete(`/admin/cards/${id}`); load(); toast.success("Deleted"); };

  return (
    <div>
      <button onClick={() => setEditing({ active: false, disclosure_required: true, aa_score: 0 })} data-testid="admin-card-new" className="inline-flex items-center gap-1.5 mb-4 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add card</button>

      {editing && (
        <div className="border border-primary/40 bg-blue-50/20 p-5 mb-6" data-testid="admin-card-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-700">{editing.id ? "Edit card" : "New card"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {CARD_TEXT.map(([k, l]) => <Field key={k} label={l} value={editing[k]} onChange={(v) => setEditing({ ...editing, [k]: v })} />)}
          </div>
          <div className="mt-3"><Field label="Airline benefits (summary)" value={editing.airline_benefits} onChange={(v) => setEditing({ ...editing, airline_benefits: v })} textarea /></div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            {CARD_URLS.map(([k, l]) => <Field key={k} label={l} value={editing[k]} onChange={(v) => setEditing({ ...editing, [k]: v })} />)}
          </div>
          <div className="flex flex-wrap items-center gap-5 mt-4">
            <label className="flex items-center gap-2 text-sm"><span className="text-xs font-mono uppercase text-muted-foreground">AA Score</span>
              <input type="number" value={editing.aa_score ?? 0} onChange={(e) => setEditing({ ...editing, aa_score: Number(e.target.value) })} className="w-20 border border-zinc-300 px-2 py-1.5 text-sm" data-testid="admin-card-score" /></label>
            {CARD_BOOLS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })} data-testid={`admin-card-${k}`} /> {l}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="admin-card-save" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-600"><Save className="w-4 h-4" /> Save card</button>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 divide-y divide-zinc-100">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`admin-card-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-600 truncate">{c.airline_name} — {c.card_name || <span className="text-muted-foreground italic">(shell, no card yet)</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{c.loyalty_program} · {c.issuer || "—"} · score {c.aa_score ?? 0} · {c.affiliate_url ? "affiliate ✓" : "no affiliate URL"}</div>
            </div>
            {c.active ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-600"><Check className="w-3.5 h-3.5" /> Live</span>
              : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Ban className="w-3.5 h-3.5" /> Hidden</span>}
            <button onClick={() => setEditing(c)} data-testid={`admin-card-edit-${c.id}`} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/partners").then((r) => setItems(r.data.items)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name is required"); return; }
    try { await api.post("/admin/partners", editing); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete partner?")) return; await api.delete(`/admin/partners/${id}`); load(); };

  return (
    <div>
      <button onClick={() => setEditing({ active: false, category: "Hotels", aa_score: 0 })} data-testid="admin-partner-new" className="inline-flex items-center gap-1.5 mb-4 px-3 py-2 bg-primary text-white text-sm font-600"><Plus className="w-4 h-4" /> Add partner</button>
      {editing && (
        <div className="border border-primary/40 bg-blue-50/20 p-5 mb-6" data-testid="admin-partner-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <label className="block"><span className="text-xs font-mono uppercase text-muted-foreground">Category</span>
              <select value={editing.category || "Hotels"} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm bg-white">
                {["Hotels", "Cards", "Cars", "Insurance", "Other"].map((c) => <option key={c}>{c}</option>)}
              </select></label>
            <Field label="Affiliate network" value={editing.affiliate_network} onChange={(v) => setEditing({ ...editing, affiliate_network: v })} />
            <Field label="Logo URL" value={editing.logo_url} onChange={(v) => setEditing({ ...editing, logo_url: v })} />
            <Field label="Official URL" value={editing.official_url} onChange={(v) => setEditing({ ...editing, official_url: v })} />
            <Field label="Affiliate URL (tracked)" value={editing.affiliate_url} onChange={(v) => setEditing({ ...editing, affiliate_url: v })} />
          </div>
          <div className="mt-3"><Field label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} textarea /></div>
          <div className="flex flex-wrap items-center gap-5 mt-4">
            <label className="flex items-center gap-2 text-sm"><span className="text-xs font-mono uppercase text-muted-foreground">AA Score</span>
              <input type="number" value={editing.aa_score ?? 0} onChange={(e) => setEditing({ ...editing, aa_score: Number(e.target.value) })} className="w-20 border border-zinc-300 px-2 py-1.5 text-sm" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="admin-partner-active" /> Active (show publicly)</label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="admin-partner-save" className="px-4 py-2 bg-primary text-white text-sm font-600">Save partner</button>
          </div>
        </div>
      )}
      <div className="border border-zinc-200 divide-y divide-zinc-100">
        {items.length === 0 && <div className="p-4 text-sm text-muted-foreground">No partners yet. Add Expedia (Hotels) here to power the Hotels page.</div>}
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`admin-partner-row-${p.id}`}>
            <div className="min-w-0 flex-1"><div className="font-600 truncate">{p.name}</div><div className="text-xs text-muted-foreground">{p.category} · {p.affiliate_network || "—"} · {p.affiliate_url ? "affiliate ✓" : "no URL"}</div></div>
            {p.active ? <span className="text-xs text-emerald-600 font-600">Live</span> : <span className="text-xs text-muted-foreground">Hidden</span>}
            <button onClick={() => setEditing(p)} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClicksTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/clicks").then((r) => setData(r.data)).catch(() => setData({ total: 0, by_link: [], recent: [] })); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-clicks">
      <div className="font-heading font-900 text-2xl">{data.total.toLocaleString()} <span className="text-sm font-mono uppercase text-muted-foreground">total clicks</span></div>
      <div>
        <h3 className="font-600 mb-2">Top links</h3>
        <div className="border border-zinc-200 divide-y divide-zinc-100">
          {data.by_link.length === 0 && <div className="p-3 text-sm text-muted-foreground">No clicks recorded yet.</div>}
          {data.by_link.map((b) => (
            <div key={b.slug} className="flex items-center gap-3 p-2.5 text-sm">
              <span className="flex-1 truncate">{b.airline_name} — {b.card_name || b.slug}</span>
              <span className="font-mono tabular-nums">{b.clicks}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
